// ═══════════════════════════════════════════════════════════════════════════
// prfQuotePrint.js — the PRF quoting package, rendered for print.
//
// What an agent puts in front of a grower: every block on the policy priced at
// the quote year, what it cost last year beside it, the coverage ladder, and
// the historical record the conversation actually turns on.
//
// ── IT PRINTS ITS OWN PROVENANCE, ON EVERY PAGE ───────────────────────────
//
// A PRF premium is decided by (crop year x grid x county x intended use x
// practice band x interval x coverage level), and a sheet that shows the money
// without the identity is a number nobody can check three months later. The
// target-sheet work already paid for that lesson: a pencilled "+15" that could
// have been Batavia's or LeRoy's was worth $3,740. So every block prints its
// county base value, the band that produced it, the rate per interval, and the
// crop year those rates came from.
//
// ── WHAT IT MUST NOT DO ───────────────────────────────────────────────────
//
//   * NO RECOMMENDATION. No level marked best, no interval ranked, no "we
//     suggest". The coverage ladder is a price list with the current level
//     marked, which is what a quote is.
//   * NO NUMBER THAT IS NOT ON SCREEN. Everything here is handed in already
//     priced by the shared engines; this file formats and does not compute.
//     The one arithmetic it does is per-acre division, and it is labelled.
//   * NO SILENT FALLBACK. A block the quote year cannot price says so on the
//     page rather than quietly printing last year's figures under this year's
//     heading.
//
// PURE — returns an HTML string. No fetch, no React, no DOM. The caller opens
// a window and prints it, the pattern the LRP monthly summary and the grain
// target sheet already use.
// ═══════════════════════════════════════════════════════════════════════════

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const D0 = (v) => (v == null || !Number.isFinite(Number(v)) ? "—"
  : "$" + Math.round(Number(v)).toLocaleString("en-US"));
const D2 = (v) => (v == null || !Number.isFinite(Number(v)) ? "—"
  : "$" + Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const PC = (v) => (v == null || !Number.isFinite(Number(v)) ? "—" : (Number(v) * 100).toFixed(0) + "%");
const NUM = (v, d = 2) => (v == null || !Number.isFinite(Number(v)) ? "—"
  : Number(v).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }));

// A signed money figure with the sign OUTSIDE the currency symbol — "$-1.50"
// reads as a typo on a sheet a grower is holding.
const SIGNED = (v, cents) => {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  const body = cents ? D2(Math.abs(n)) : D0(Math.abs(n));
  return (n > 0 ? "+" : n < 0 ? "−" : "") + body;
};

// ⚠ NOT EVERY COMPARISON ROW IS MONEY. `compareYears` pushes an ACRES row with
// unit "ac", and the change column formatted every row with SIGNED — so a farm
// whose acreage did not move printed "Acres 185.47 -> 185.47, $0.00". Kevin
// caught it on the live sheet. The value columns had always branched on
// `r.unit`; the change column beside them had not, so the document contradicted
// itself one column apart — and the SCREEN was right the whole time, which is
// the "screen and document ask the same question" rule failing on the document.
const SIGNED_UNIT = (v, dp, unit) => {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  return (n > 0 ? "+" : n < 0 ? "−" : "") + NUM(Math.abs(n), dp) + (unit ? " " + unit : "");
};

// ONE SOURCE FOR THE PAGE SETUP. The @page rule and anything that reasons about how
// much fits on a sheet have to agree, and keeping them as two numbers is how a margin
// change silently invalidates a budget — check-screens had exactly that defect today,
// reporting a 0.5in sheet for a document declaring 0.4in.
export const PAGE_MARGIN_IN = 0.5;
export const SHEET_PX = Math.round((11 - 2 * PAGE_MARGIN_IN) * 96);   // letter portrait @96dpi

// Measured 2026-10-13 on the live Brian Thompson package, varying ONLY the year-row
// count: 5 -> 215px, 10 -> 295, 20 -> 455, 40 -> 775, 60 -> 1095, 78 -> 1383. Linear at
// 16.0px per row over 135px of chrome, and 135 + 60 x 16 = 1095 reproduces the series.
export const MX_ROW_PX = 16;
export const MX_CHROME_PX = 135;
export const mxRowsPerPage = () => Math.max(1, Math.floor((SHEET_PX - MX_CHROME_PX) / MX_ROW_PX));

const CSS = `
  @page { size: letter portrait; margin: ${PAGE_MARGIN_IN}in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #1a2e1a; margin: 0; font-size: 11px; }
  .pkg { max-width: 7.5in; margin: 0 auto; }
  /* ⚠ BREAK BEFORE, NOT AFTER. A page-break-after:always with a :last-child
     exemption does not exempt the last PAGE — the footer div is the last child
     of .pkg, so every .page kept its forced break and the disclaimer printed
     alone on a page of its own. Measured: 2 pages of content came out as a
     3-page PDF. Breaking BEFORE each page but the first leaves the footer to
     flow after the last one.
     (And no backticks in here: this block is a JS template literal.) */
  .page { page-break-before: always; }
  .page:first-child { page-break-before: avoid; }
  h1 { font-size: 17px; margin: 0 0 2px; letter-spacing: .01em; }
  h2 { font-size: 13px; margin: 16px 0 6px; border-bottom: 1.5px solid #2d4a2d; padding-bottom: 3px; }
  .sub { color: #5a6b5a; font-size: 10.5px; margin-bottom: 10px; }
  table { border-collapse: collapse; width: 100%; font-size: 10.5px; }
  th { text-align: right; padding: 4px 6px; border-bottom: 1px solid #2d4a2d;
       font-size: 8.5px; text-transform: uppercase; letter-spacing: .05em; color: #5a6b5a; }
  th.l, td.l { text-align: left; }
  td { text-align: right; padding: 4px 6px; border-bottom: 1px solid #e0eae0; font-variant-numeric: tabular-nums; }
  tr.cur td { background: #eef4ee; font-weight: 700; }
  .hero { display: flex; gap: 22px; flex-wrap: wrap; margin: 8px 0 4px;
          background: #f7faf7; border: 1px solid #cfe0cf; border-radius: 6px; padding: 10px 12px; }
  .hero div span { display: block; font-size: 8.5px; text-transform: uppercase;
                   letter-spacing: .05em; color: #5a6b5a; }
  .hero div b { font-size: 18px; font-variant-numeric: tabular-nums; }
  .prov { font-size: 9px; color: #5a6b5a; margin-top: 6px; line-height: 1.5; }
  .warn { background: #fff8ec; border: 1px solid #e8d9b8; color: #8a5a00;
          border-radius: 5px; padding: 6px 8px; font-size: 10px; margin: 6px 0; line-height: 1.45; }
  .foot { margin-top: 14px; padding-top: 8px; border-top: 1px solid #cfe0cf;
          font-size: 9px; color: #5a6b5a; line-height: 1.5; }
  .m td, .m th { padding: 2px 3px; font-size: 8px; }
  .m td.y { text-align: left; font-weight: 700; }
  .el { background: #eef4ee; }
  /* the sub-figure under a loss ratio: the window it covers, and how many of
     those years actually paid. A ratio with no stated sample is not checkable. */
  .hy { font-size: 7.5pt; color: #6b7d6b; font-weight: 400; margin-top: 1px; }
  .na td { color: #8a978a; font-style: italic; }
  /* the paid-in / took-out answer. It is the one figure a grower reads first,
     so it is not a row in the ladder table — it is the top of the page. */
  .ans { border: 1px solid #cfe0cf; border-radius: 6px; margin: 8px 0 4px; overflow: hidden; }
  .ans table { font-size: 11px; }
  .ans th { background: #f7faf7; border-bottom: 1px solid #cfe0cf; padding: 5px 8px; }
  .ans td { padding: 7px 8px; }
  .ans tr:last-child td { border-bottom: 0; }
  .ans .big { font-size: 14px; font-weight: 700; }
  .gain { color: #166f49; }
  .cost { color: #9c580f; }
  figure { margin: 8px 0 4px; }
  figcaption { font-size: 9px; color: #5a6b5a; line-height: 1.5; margin-top: 3px; }
  svg { display: block; max-width: 100%; }
`;

// ── CHARTS — inline SVG, no library, every shape with an explicit fill ──
//
// A printed sheet has no runtime and a PDF export may drop CSS backgrounds, so
// every mark is drawn as a filled SVG shape rather than a styled div. These
// plot figures the CALLER already computed; the only arithmetic is the pixel
// mapping, and every axis label names a value the chart actually reaches.
const sesc = (s) => esc(s);

// A year-by-year bar of what the election would have paid, with the annual
// premium drawn across it. The loss ratio says how much came back per dollar;
// it cannot say whether that was one enormous year or a steady dozen, and those
// are different risks. This is the only thing on the sheet that shows the shape.
function yearBars(pts, premium, opts = {}) {
  const n = (pts || []).length;
  if (!n) return "";
  const W = 700, H = 140, L = 52, R = 10, T = 14, B = 20;
  const pw = W - L - R, ph = H - T - B;
  const hi = Math.max(premium || 0, ...pts.map((p) => p.indemnity || 0));
  if (!(hi > 0)) return "";
  const top = hi * 1.08;
  const y = (v) => T + ph - (v / top) * ph;
  const bw = pw / n, bar = Math.max(1.2, bw - (bw > 6 ? 1.6 : 0.5));

  const marks = pts.map((p, i) => {
    const x = L + i * bw + (bw - bar) / 2;
    const v = p.indemnity || 0;
    // A YEAR THAT PAID NOTHING IS STILL A YEAR. Drawn as nothing it reads as a
    // gap in the record; a stub on the baseline says "this year, no payment".
    if (!(v > 0)) return `<rect x="${x.toFixed(1)}" y="${(T + ph - 1.5).toFixed(1)}" width="${bar.toFixed(1)}" height="1.5" fill="#d7e3d7"/>`;
    const yy = y(v);
    return `<rect x="${x.toFixed(1)}" y="${yy.toFixed(1)}" width="${bar.toFixed(1)}" height="${(T + ph - yy).toFixed(1)}" fill="#2f6b4a"/>`;
  }).join("");

  // year labels: first, last, and every 10th in between — enough to locate a
  // bar without turning the axis into a wall of digits.
  // ⚠ A DECADE TOO CLOSE TO EITHER END IS DROPPED. Grid 27808's record starts
  // 1948, so "1948" and "1950" are two bars apart and printed on top of each
  // other — the axis read as a smudge.
  const MINGAP = 26;
  const lab = pts.map((p, i) => {
    const yr = Number(p.year);
    const end = i === 0 || i === n - 1;
    if (!end && yr % 10 !== 0) return "";
    if (!end && (i * bw < MINGAP || (n - 1 - i) * bw < MINGAP)) return "";
    const x = L + i * bw + bw / 2;
    const anchor = i === 0 ? "start" : i === n - 1 ? "end" : "middle";
    return `<text x="${x.toFixed(1)}" y="${H - 6}" font-size="8" fill="#5a6b5a" text-anchor="${anchor}">${sesc(yr)}</text>`;
  }).join("");

  // ⚠ THE PREMIUM LABEL LIVES IN THE GUTTER, NOT ON THE PLOT. Set at the right
  // edge it printed straight through the tall bars of the last decade.
  const py = premium > 0 ? y(premium) : null;
  const pline = py == null ? "" :
    `<line x1="${L}" y1="${py.toFixed(1)}" x2="${W - R}" y2="${py.toFixed(1)}" stroke="#9c580f" stroke-width="1" stroke-dasharray="3 2"/>` +
    `<text x="${L - 6}" y="${(py + 3).toFixed(1)}" font-size="8" fill="#9c580f" text-anchor="end">${D0(premium)}</text>`;

  // ⚠ THE TOP LABEL NAMES A VALUE THE CHART REACHES. Labelling the 8% headroom
  // puts a figure on the axis that no bar corresponds to.
  const ymax = y(hi);
  return `<figure>
    <svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" role="img"
         aria-label="Indemnity by year against the annual premium">
      <rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>
      <line x1="${L}" y1="${ymax.toFixed(1)}" x2="${W - R}" y2="${ymax.toFixed(1)}" stroke="#eaf1ea" stroke-width="1"/>
      <line x1="${L}" y1="${T + ph}" x2="${W - R}" y2="${T + ph}" stroke="#cfe0cf" stroke-width="1"/>
      <text x="${L - 6}" y="${(ymax + 3).toFixed(1)}" font-size="8" fill="#5a6b5a" text-anchor="end">${D0(hi)}</text>
      <text x="${L - 6}" y="${T + ph + 3}" font-size="8" fill="#5a6b5a" text-anchor="end">$0</text>
      ${marks}${pline}${lab}
    </svg>
    ${opts.caption ? `<figcaption>${opts.caption}</figcaption>` : ""}
  </figure>`;
}

// The election itself, as a shape rather than three percentages. Which months a
// grower insured is the whole decision being quoted, and a column of numbers
// does not show that it is weighted to summer.
function allocBars(rows) {
  const list = (rows || []).filter((r) => Number(r.allocation) > 0);
  if (!list.length) return "";
  const hi = Math.max(...list.map((r) => Number(r.allocation)));
  if (!(hi > 0)) return "";
  const W = 700, RH = 17, L = 74, R = 46, T = 2;
  const H = T + list.length * RH + 2, pw = W - L - R;
  const bars = list.map((r, i) => {
    const yy = T + i * RH + 2, w = (Number(r.allocation) / hi) * pw;
    return `<text x="0" y="${yy + 9}" font-size="9" fill="#1a2e1a" font-weight="600">${sesc(r.interval)}</text>` +
      `<rect x="${L}" y="${yy}" width="${Math.max(1, w).toFixed(1)}" height="11" fill="#2f6b4a"/>` +
      `<text x="${W - R + 6}" y="${yy + 9}" font-size="9" fill="#1a2e1a" text-anchor="start">${PC(r.allocation)}</text>`;
  }).join("");
  return `<figure>
    <svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" role="img"
         aria-label="Share of value by interval">
      <rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>${bars}
    </svg>
  </figure>`;
}

// The price list, drawn. The ladder table states protection and premium at each
// level and hides the one thing a grower asks about them: they do not move
// together. On this policy 70% -> 90% buys 29% more protection for 310% more
// premium, which is four columns of arithmetic in the table and one shape here.
//
// ⚠ TWO SCALES, NEVER ONE. Protection per acre and premium per acre differ by a
// factor of ~30; on a shared axis every premium bar is a hairline and the chart
// says nothing. Each row carries its own axis and its own maximum label.
function ladderBars(levels, acres) {
  const live = (levels || []).filter((l) => l.available && Number.isFinite(Number(l.protection))
    && Number.isFinite(Number(l.producer_premium)));
  if (live.length < 2 || !(Number(acres) > 0)) return "";
  const ac = Number(acres);
  const W = 700, L = 96, R = 8, RH = 15, GAP = 12;
  const pw = W - L - R;
  const series = [
    { name: "Protection / acre", get: (l) => l.protection / ac, fmt: D0, fill: "#8ab89a" },
    { name: "Premium / acre", get: (l) => l.producer_premium / ac, fmt: D2, fill: "#2f6b4a" },
  ];
  const H = series.length * (14 + live.length * RH) + GAP;
  let yy = 0;
  const body = series.map((s) => {
    const vals = live.map(s.get);
    const hi = Math.max(...vals);
    // ⚠ NO "max" LABEL. It is always the same number as the longest bar's own
    // label, and a figure printed twice invites a reader to check one against
    // the other and learn nothing. The caption carries the per-row-scale rule.
    const head = `<text x="0" y="${yy + 9}" font-size="8.5" fill="#5a6b5a" letter-spacing="0.5">${sesc(s.name.toUpperCase())}</text>`;
    yy += 14;
    const rows = live.map((l, i) => {
      const v = vals[i], w = hi > 0 ? (v / hi) * (pw - 54) : 0;
      const ty = yy + i * RH;
      return `<text x="0" y="${ty + 9}" font-size="9" fill="#1a2e1a" font-weight="${l.is_current ? 700 : 400}">` +
        `${PC(l.coverage)}${l.is_current ? " •" : ""}</text>` +
        `<rect x="${L}" y="${ty + 1}" width="${Math.max(1, w).toFixed(1)}" height="10" fill="${s.fill}"/>` +
        `<text x="${(L + Math.max(1, w) + 5).toFixed(1)}" y="${ty + 9}" font-size="8.5" fill="#1a2e1a">${s.fmt(v)}</text>`;
    }).join("");
    yy += live.length * RH + GAP;
    return head + rows;
  }).join("");
  return `<figure>
    <svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" role="img"
         aria-label="Protection and producer premium per acre at each coverage level">
      <rect x="0" y="0" width="${W}" height="${H}" fill="#ffffff"/>${body}
    </svg>
    <figcaption>Each row is drawn to its OWN scale — protection per acre and premium per acre
      differ by about thirty times, so one axis would flatten the premium row to a hairline.
      • marks the level on this quote.</figcaption>
  </figure>`;
}

// ── one block ──────────────────────────────────────────────────────────
// `b` is already priced by the caller — see prfRenewalQuote. This formats.
function blockSection(b) {
  const q = b.quote;
  // ⚠ NO RATE COMPARISON. Kevin, 2026-09-02: "I dont need to compare rates, I
  // need to compare premium per acre." The rate columns were also empty in
  // practice — the policy year's rate table is sparse, so the left-hand column
  // printed as em-dashes — but they are gone because they were the wrong
  // question, not because they were blank.
  // ⚠ THE INTERVAL TABLE IS GONE, NOT MOVED. `allocBars` carries the label and
  // the percentage inside the drawing, so a table beside it stated every figure
  // twice — and a reader checking one against the other learns nothing.
  const cmpRows = (b.compare?.rows || []).map((r) => `
    <tr>
      <td class="l">${esc(r.label)}</td>
      <td>${r.unit === "$" ? (r.cents ? D2(r.last) : D0(r.last)) : NUM(r.last, r.cents ? 2 : 0)}</td>
      <td>${r.unit === "$" ? (r.cents ? D2(r.next) : D0(r.next)) : NUM(r.next, r.cents ? 2 : 0)}</td>
      <td>${!r.comparable ? "not comparable"
            : r.delta == null ? "—"
            : (r.unit === "$" ? SIGNED(r.delta, !!r.cents)
                              : SIGNED_UNIT(r.delta, r.cents ? 2 : 0, r.unit))
              + (r.pct != null ? ` (${(r.pct * 100).toFixed(1)}%)` : "")}</td>
    </tr>`).join("");

  // ⚠ BOTH PER-ACRE COLUMNS ARE NOW UNCONDITIONAL, which REVERSES an earlier
  // decision — recorded rather than quietly overwritten. The column used to be
  // dropped wherever the bar chart was drawn, on the reasoning that the chart
  // already labels every bar with its exact premium per acre and a column beside
  // it states the same number twice.
  //
  // Kevin, 2026-10-05: "can you add a column for per acre protection and a column
  // for per acre premium to the table at the bottom of the first page." The
  // duplication is real and it is wanted: the chart is a visual comparison and the
  // table is the price list somebody reads a figure off, and a grower comparing
  // levels reads down a column rather than across a chart. Protection per acre was
  // never shown at all at any level but the quoted one.
  //
  // ⚠ THE DENOMINATOR IS SAFE BECAUSE THE LADDER HOLDS THE ALLOCATION CONSTANT.
  // `coverageLadder` prices the SAME alloc at every level — only the coverage
  // level moves — so one acreage divides every row. That is the footnote this page
  // already prints, and it is what makes a per-acre column meaningful rather than a
  // mix of denominators. With no acreage on the block both columns read "—"; they
  // never fall back to the total, which would print a per-acre figure thirty times
  // too large and look plausible.
  const lBars = ladderBars(b.ladder?.levels, q && q.acres);
  const lAc = q && q.acres > 0 ? q.acres : null;
  const ladderRows = (b.ladder?.levels || []).filter((l) => l.available).map((l) => `
    <tr class="${l.is_current ? "cur" : ""}">
      <td class="l">${PC(l.coverage)}${l.is_current ? " — current" : ""}</td>
      <td>${D0(l.protection)}</td>
      <td>${lAc ? D0(l.protection / lAc) : "—"}</td>
      <td>${D0(l.producer_premium)}</td>
      <td>${lAc ? D2(l.producer_premium / lAc) : "—"}</td>
      <td>${PC(l.pay_freq)}</td>
    </tr>`).join("");

  return `
  <div class="page">
    <h1>PRF quote — ${esc(b.farmer)}</h1>
    <div class="sub">
      ${esc(b.policyNumber)} · grid <b>${esc(b.grid)}</b> · ${esc(b.county)} · ${esc(b.use)}
      · crop year <b>CY${esc(b.year)}</b>
    </div>

    ${q && q.ok !== false ? `
      <div class="hero">
        <div><span>Acres</span><b>${NUM(q.acres, 2)}</b></div>
        <div><span>Protection</span><b>${D0(q.protection)}</b></div>
        <div><span>Producer premium</span><b>${D0(q.producer_premium)}</b></div>
        <div><span>Protection / acre</span><b>${D0(q.protection_per_acre)}</b></div>
        <div><span>Premium / acre</span><b>${D2(q.premium_per_acre)}</b></div>
      </div>
      <div class="prov">
        Priced at <b>CY${esc(b.year)}</b> rates · county base value <b>${D0(q.cbv)}/ac</b>
        ${b.band != null ? ` · practice band <b>${esc(b.band)}xx</b>` : ""}
        · coverage <b>${PC(b.coverage)}</b> · productivity <b>${PC(b.productivity)}</b>
        ${b.cbvSource ? ` · ${esc(b.cbvSource)}` : ""}
      </div>`
    : `<div class="warn"><b>Not quoted.</b> ${esc(b.reason || "RMA published no rate for this structure.")}</div>`}

    <h2>Intervals elected</h2>
    ${(b.intervals || []).length
      ? allocBars(b.intervals)
      : `<table><thead><tr><th class="l">Interval</th><th>% of value</th></tr></thead>
         <tbody><tr><td class="l" colspan="2">No intervals elected.</td></tr></tbody></table>`}

    ${cmpRows ? `
      <h2>What it cost last year, and what it costs now</h2>
      <table>
        <thead><tr><th class="l"></th><th>CY${esc(b.baselineYear)} as billed</th>
          <th>CY${esc(b.year)} quoted</th><th>Change</th></tr></thead>
        <tbody>${cmpRows}</tbody>
      </table>
      <div class="prov">CY${esc(b.baselineYear)} is the carrier's own figure — liability and gross
        premium as stored on the policy. The producer share of it is gross × (1 − RMA's published
        subsidy for this coverage level), which reconciles to the AIP's policy total to the dollar.</div>
      ${b.compare?.acres_changed ? `<div class="warn">The acreage changed, so the TOTALS are not a
        like-for-like comparison. The per-acre rows are the ones that answer whether PRF got
        cheaper.</div>` : ""}` : ""}

    ${ladderRows ? `
      <h2>Coverage levels at CY${esc(b.year)} rates</h2>
      ${lBars}
      <table>
        <thead><tr><th class="l">Level</th><th>Protection</th><th>Protection / acre</th>
          <th>Producer premium</th><th>Premium / acre</th><th>How often it paid</th></tr></thead>
        <tbody>${ladderRows}</tbody>
      </table>
      <div class="prov">No level is marked as the answer. The allocation is held constant across the
        ladder; only the coverage level moves.</div>` : ""}
  </div>`;
}

// ── the historical record ──────────────────────────────────────────────
// One page, the view the caller chose. Elected intervals are shaded; every
// other column is a counterfactual the policy never carried.
function matrixSection(m, opts) {
  if (!m || !m.available || !m.rows?.length) return "";
  const view = m.views.find((v) => v.id === opts.view) || m.views[0];
  const cell = (c) => {
    if (view.id === "index") return c.index == null ? "—" : (c.index * 100).toFixed(0) + "%";
    if (view.id === "factor") return c.factor == null ? "—" : (c.factor * 100).toFixed(0) + "%";
    if (view.id === "indemnity") return c.indemnity_per_acre == null ? "—" : "$" + Math.round(c.indemnity_per_acre);
    // rainfall: BOTH halves or neither — never index x normal
    return c.rainfall_in == null ? "—" : NUM(c.rainfall_in, 2);
  };
  const head = m.intervals.map((i) =>
    `<th class="${i.elected ? "el" : ""}">${i.elected ? "• " : ""}${esc(i.interval)}</th>`).join("");
  const rowHtml = (r) => `
    <tr><td class="y">${esc(r.year)}</td>${r.cells.map((c) =>
      `<td class="${c.elected ? "el" : ""}">${cell(c)}</td>`).join("")}</tr>`;
  // ⚠ CHUNKED BY A MEASURED ROW BUDGET, NOT TRUNCATED. Every year still prints; what
  // changes is that no table is cut in half. A span the agent narrowed on screen is
  // usually one chunk and renders exactly as before.
  const PER = mxRowsPerPage();
  const chunks = [];
  for (let i = 0; i < m.rows.length; i += PER) chunks.push(m.rows.slice(i, i + PER));
  if (!chunks.length) chunks.push([]);
  // ── LOSS RATIO PER COLUMN, OVER EXACTLY THE YEARS PRINTED ABOVE ─────────
  // Kevin, 2026-10-05: "at the bottom of each column could you show a histroical
  // loss ratio figure that updates based on which data is being displayed."
  //
  // It is NOT recomputed here. The caller passes the figures it already rendered on
  // screen, which come from `intervalHistory`'s `replay_loss_ratio` — mean payment
  // factor over the producer rate, protection cancelling, so it is comparable across
  // intervals and needs no allocation. A second derivation on the print path is how a
  // sheet and the screen it was printed from come to state different numbers.
  //
  // ⚠ AN INTERVAL RMA DOES NOT RATE HERE HAS NO RATIO, AND PRINTS "—", NEVER 0%.
  // A zero would read as "this interval never paid", which is a claim about rainfall;
  // the fact is that there is no published rate to divide by.
  const lr = opts.lossRatio || null;
  // ⚠ THE FOOTER SPANS EVERY YEAR PRINTED, NOT THE CHUNK IT SITS UNDER. It is a replay
  // "over exactly the years printed above", so once the table is split it belongs on the
  // LAST page and has to name its span — a reader looking at that page alone would
  // otherwise take a 78-year ratio for the 27 rows in front of them.
  const footSpan = m.rows.length
    ? `${m.rows[m.rows.length - 1].year}\u2013${m.rows[0].year}`
    : "";
  const foot = lr ? `
      <tfoot><tr><td class="l">Loss ratio${chunks.length > 1 ? `<br><span style="font-weight:400;color:#5a6b5a">${footSpan}, all ${m.rows.length} yrs</span>` : ""}</td>${m.intervals.map((i) => {
        const v = lr[i.interval];
        return `<td class="${i.elected ? "el" : ""}">${v == null ? "—" : Math.round(v * 100) + "%"}</td>`;
      }).join("")}</tr></tfoot>` : "";
  // One .page per chunk. The heading, the column head and the provenance note repeat on
  // each, because a continuation sheet with no header is a table of numbers whose columns
  // nobody can identify.
  return chunks.map((rows, ci) => {
    const last = ci === chunks.length - 1;
    const span = rows.length ? `${rows[rows.length - 1].year}\u2013${rows[0].year}` : "";
    return `
  <div class="page">
    <h1>Historical record — grid ${esc(opts.grid)}${chunks.length > 1 ? ` (${ci + 1} of ${chunks.length})` : ""}</h1>
    <div class="sub">${esc(view.label)} · ${chunks.length > 1 ? esc(span) : esc(m.year_range?.from) + "\u2013" + esc(m.year_range?.to)}
      · ${chunks.length > 1 ? rows.length + " of " + m.rows.length : m.rows.length} year${m.rows.length === 1 ? "" : "s"} × ${m.intervals.length} interval${m.intervals.length === 1 ? "" : "s"}${opts.narrowed ? " — as laid out on screen" : ", elected or not"}</div>
    <div class="prov" style="margin-bottom:8px">${esc(view.note)}</div>
    <table class="m">
      <thead><tr><th class="l">Year</th>${head}</tr></thead>
      <tbody>${rows.map(rowHtml).join("")}</tbody>${last ? foot : ""}
    </table>
    <div class="prov">• marks an interval this policy elected — every other column is a
      counterfactual and was never insured.${last && lr ? " Loss ratio is the replay over every year printed in this record: mean payment factor divided by the producer premium rate, so it is comparable across intervals and does not depend on the allocation." : ""}
      ${opts.narrowed ? "This is the layout selected on the History tab, not the full record — the grid holds more years and intervals than are shown." : ""}</div>
  </div>`;
  }).join("");
}

// ── LOSS RATIO BY COVERAGE LEVEL ───────────────────────────────────────
// Kevin, 2026-09-05: "instead of all the historical numbers, just show the loss
// ratio table."
//
// What it replaces was the year x interval evidence matrix — 78 rows of index
// values against every interval, elected or not. It is the right thing to have
// on screen, where a reader can pick a cell, and the wrong thing to hand a
// grower: pages of counterfactual numbers with the one figure they asked about
// nowhere in it.
//
// ⚠ IT IS A REPLAY, NOT A HISTORY, AND THE PAGE SAYS SO IN ITS OWN HEADING.
// The index is RMA’s published record; the premium and protection are THIS
// year’s quote held constant across every year. So it answers "this election,
// at these rates, against what the rain actually did" — it is NOT what the
// grower’s own loss ratio was, which would need the elections they actually
// held each year and is in nothing we hold.
//
// The numbers come from the SAME helpers the screen uses (windowYears,
// lossRatioOver over the ladder rows), so the sheet and the tab cannot state
// different loss ratios for one farm.
function lossRatioSection(b, IH) {
  const cs = b && b.ladder;
  if (!cs || !cs.levels || !cs.levels.length || !IH) return "";
  const anyRow = cs.levels.find((r) => r.available && (r.years || []).length);
  if (!anyRow) return "";
  const allYears = anyRow.years || [];

  // The same three windows the tab offers. A window the record cannot fill is
  // DROPPED rather than shown short — a 10-year column built from 6 years is a
  // different statistic wearing the same heading.
  const WINDOWS = [["10", "Last 10 years"], ["20", "Last 20 years"], ["all", "Full record"]];
  const cols = [];
  for (const [id, label] of WINDOWS) {
    const win = IH.windowYears(allYears, id, null);
    if (!win || !win.length) continue;
    if (id !== "all" && win.length < Number(id)) continue;
    if (cols.some((c) => c.win.length === win.length)) continue;   // "all" == "20" on a 20-year record
    cols.push({ id, label, win });
  }
  if (!cols.length) return "";

  const LR = (v) => (v == null ? "—" : v.toFixed(2) + "×");

  // ── THE ANSWER, FOR THE LEVEL ON THIS QUOTE ───────────────────────────
  // Kevin, 2026-09-29: "If I paid in, how much wouldve I gotten out of it?"
  // A ratio is the normalised form of exactly that question, and a grower
  // holding the sheet should not have to multiply his own premium by ten to
  // get back to the dollars. Both halves and the net, per window.
  //
  // ⚠ IT IS THE CURRENT LEVEL ONLY, AND IF NO LEVEL IS MARKED CURRENT THE
  // BLOCK IS OMITTED. Picking one to headline is a recommendation, which is
  // the one thing this sheet must never do. The ladder below still prints.
  const cur = cs.levels.find((r) => r.available && r.is_current && (r.years || []).length);
  // ⚠ PER ACRE RIDES WITH EVERY TOTAL. Kevin, 2026-09-29: "I like the historial
  // data table with indemnity per acre." A grower compares a figure against his
  // own rent and inputs, which are per acre, and $628,834 across 78 years is not
  // a number anyone can weigh against anything. This is the per-acre division
  // the module has always declared — it is NOT a second unit for the same
  // column, it is a sub-line under it, so nothing is stated twice in one cell.
  const acres = Number(b.quote && b.quote.acres);
  // ⚠ SIGNED, FOR THE NET. D0(-222) is "$-222", which reads as a typo on a
  // sheet a grower is holding — the same reason SIGNED exists at all.
  const perAc = (v, signed) => (Number.isFinite(acres) && acres > 0 && v != null && Number.isFinite(Number(v))
    ? `<div class="hy">${signed ? SIGNED(Number(v) / acres) : D0(Number(v) / acres)}/ac</div>` : "");
  const answer = !cur ? "" : (() => {
    const rows = cols.map((c) => {
      const lr = IH.lossRatioOver(cur, c.win);
      if (!lr) return "";
      const net = lr.net;
      return `<tr>
        <td class="l"><b>${esc(c.label)}</b><div class="hy">${esc(c.win[0])}–${esc(c.win[c.win.length - 1])}</div></td>
        <td>${D0(lr.total_producer_premium)}${perAc(lr.total_producer_premium)}</td>
        <td class="big">${D0(lr.total_indemnity)}${perAc(lr.total_indemnity)}</td>
        <td class="big ${net == null ? "" : net >= 0 ? "gain" : "cost"}">${SIGNED(net)}${perAc(net, true)}</td>
        <td>${LR(lr.loss_ratio)}<div class="hy">${lr.years_paid} of ${lr.n_years} yrs paid</div></td>
      </tr>`;
    }).join("");
    return `<div class="ans"><table>
      <thead><tr>
        <th class="l">At ${PC(cur.coverage)} — the level on this quote</th>
        <th>You'd have paid in</th><th>You'd have taken out</th>
        <th>Net</th><th>Loss ratio</th>
      </tr></thead>
      <tbody>${rows}</tbody></table></div>
    <div class="prov">Paid in is ${D0(cur.producer_premium)} of producer premium × the number of
      years in the window. Taken out is what the elected intervals would have paid against RMA's
      published index for those same years. Net is one minus the other — it is not a return on
      an investment, it is what this coverage would have cost or returned over that stretch.
      ${acres > 0 ? `The per-acre figure under each is that whole-window total over
      ${NUM(acres, 2)} acres — not an annual figure.` : ""}</div>`;
  })();

  // ── AND THE SHAPE OF IT ───────────────────────────────────────────────
  // One enormous year and a steady dozen produce the same loss ratio and are
  // completely different risks. The widest window is charted because it is the
  // one with the most to say; it names its own range.
  const chart = !cur ? "" : (() => {
    const c = cols[cols.length - 1];
    const lr = IH.lossRatioOver(cur, c.win);
    if (!lr || !lr.by_year?.length) return "";
    return yearBars(lr.by_year, cur.producer_premium, {
      caption: `What ${PC(cur.coverage)} would have paid each year, ${esc(c.win[0])}–${esc(c.win[c.win.length - 1])}, `
        + `against the ${D0(cur.producer_premium)} it would have cost each year. `
        + `A bar above the dashed line is a year the coverage returned more than its premium; `
        + `a stub on the baseline is a year it paid nothing. ${lr.years_paid} of ${lr.n_years} years paid.`,
    });
  })();

  const head = cols.map((c) =>
    `<th>${esc(c.label)}<div class="hy">${esc(c.win[0])}–${esc(c.win[c.win.length - 1])}</div></th>`).join("");
  // ⚠ A LEVEL RMA DOES NOT PUBLISH IS NAMED, NOT DROPPED. Filtered out, the
  // ladder simply stops at 90% and the reader is left to wonder whether 95% was
  // omitted, unavailable, or worse than the row above it. It is a row with its
  // reason in it.
  const rows = cs.levels.map((r) => {
    if (!r.available) {
      return `<tr class="na"><td class="y">${(r.coverage * 100).toFixed(0)}%</td>` +
        `<td colspan="${2 + cols.length}">not offered — ${esc(r.reason || "no published rate at this level")}</td></tr>`;
    }
    const cells = cols.map((c) => {
      const lr = IH.lossRatioOver(r, c.win);
      if (!lr) return `<td>—</td>`;
      // ⚠ THE NET DOLLARS RIDE WITH THE RATIO. A ratio hides scale: 0.90× on a
      // $6,106 premium and 0.90× on a $1,490 one are the same number and a
      // $4,600 difference in what the level would have cost over the window.
      return `<td><b>${LR(lr.loss_ratio)}</b>` +
        `<div class="hy ${lr.net == null ? "" : lr.net >= 0 ? "gain" : "cost"}">${SIGNED(lr.net)}</div>` +
        `<div class="hy">${lr.years_paid} of ${lr.n_years} yrs paid</div></td>`;
    }).join("");
    return `<tr class="${r.is_current ? "el" : ""}">
      <td class="y">${(r.coverage * 100).toFixed(0)}%${r.is_current ? " •" : ""}</td>
      <td>$${Math.round(r.producer_premium).toLocaleString("en-US")}</td>
      <td>$${Math.round(r.protection).toLocaleString("en-US")}</td>
      ${cells}</tr>`;
  }).join("");

  return `
  <div class="page">
    <h1>If this election had been in force</h1>
    <div class="sub">What it would have paid, and what it would have cost — grid ${esc(b.grid)}${b.county ? " · " + esc(b.county) : ""}</div>
    ${answer}
    ${chart}
    <h2>Every coverage level</h2>
    <table class="m">
      <thead><tr><th class="l">Coverage</th><th>Your premium</th><th>Protection</th>${head}</tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="prov">• marks the level on this quote. Each cell is the loss ratio, the net dollars
      over that window, and how many of those years paid. Loss ratio is indemnity divided by the
      PRODUCER premium — what you pay, not the gross — so 1.00× means the coverage returned
      exactly what it cost. Above 1.00× it paid more than it cost over that window.</div>
    <div class="prov"><b>This is a replay, not your history.</b> The rainfall index is RMA’s
      published record; the premium and protection are THIS year’s quote held constant across
      every year shown. It answers what this election would have done against the rain that
      actually fell — it is not the loss ratio on the coverage you held at the time.</div>
  </div>`;
}

// ── the package ────────────────────────────────────────────────────────
export function renderPrfQuotePackage(pkg = {}) {
  const blocks = (pkg.blocks || []).map(blockSection).join("");
  // ⚠ THE EVIDENCE MATRIX IS NO LONGER PRINTED BY DEFAULT. matrixSection is kept
  // and still renders when a caller asks for it (`includeMatrix`), because the
  // year x interval record is the right thing to reach for when a grower
  // questions a specific year. What it is not is the front of a quote package.
  const matrix = (pkg.includeMatrix && pkg.matrix)
    ? matrixSection(pkg.matrix, { view: pkg.matrixView || "factor", grid: pkg.matrixGrid,
        // Carried, never recomputed — see the note in matrixSection.
        lossRatio: pkg.matrixLossRatio || null, narrowed: !!pkg.matrixNarrowed }) : "";
  const lossRatio = (pkg.blocks || []).map((b) => lossRatioSection(b, pkg.IH)).join("");
  const printed = pkg.printedOn || "";
  return `<!doctype html><html><head><meta charset="utf-8">
<title>PRF quote — ${esc(pkg.farmer || "")}</title><style>${CSS}</style></head>
<body><div class="pkg">
${blocks}${lossRatio}${matrix}
<div class="foot">
  <b>${esc(pkg.agency || "Clover Ag Risk Management")}</b>${pkg.agent ? " · " + esc(pkg.agent) : ""}
  ${printed ? " · prepared " + esc(printed) : ""}<br>
  Premium and protection are computed from RMA's published actuarial tables for the crop year shown
  and are an estimate prepared by your agent, not a bound quote. A PRF rate is identified by crop
  year, grid, county, intended use, practice band, interval and coverage level; the identity behind
  every figure here is printed beside it. Rainfall-index outcomes are not measured forage losses.
  Verify against the Schedule of Insurance before signing.
</div>
</div>
<script>try{window.focus();window.print();}catch(e){}</script>
</body></html>`;
}
